import { TransformState } from '../../state/types';
import { GT_COMPONENT_TYPES } from '../../utils/constants/gt/constants';
import * as t from '@babel/types';
import type { NodePath } from '@babel/traverse';
import { getObjectPropertyFromObjectExpression } from '../../utils/parsing/getObjectPropertyFromObjectExpression';
import { validateExpressionIsStringLiteral } from '../../utils/validation/validateExpressionIsStringLiteral';
import type { JsxChildren } from '@generaltranslation/format/types';
import { constructJsxChildren } from '../jsx-children/constructJsxChildren';
import { validateChildrenPropertyFromObjectExpression } from '../../utils/validation/validateChildrenFromObjectExpression';
import { validateExpressionIsNumericLiteral } from '../../utils/validation/validateExpressionIsNumericLiteral';
import { validateExpressionIsBooleanLiteral } from '../../utils/validation/validateExpressionIsBooleanLiteral';
import { validateDerive } from './validateTranslationFunction';
import { JsxValidationError } from '../jsx-children/errors';
/**
 * Given a translation component, validate the arguments
 */
export function validateTranslationComponentArgs(
  callExprPath: NodePath<t.CallExpression>,
  canonicalName: string,
  state: TransformState
): {
  errors: string[];
  _hash?: string;
  id?: string;
  context?: string;
  maxChars?: number;
  requiresReview?: boolean;
  children?: JsxChildren;
  hasDeriveContext?: boolean;
  hasDeriveChildren?: boolean;
} {
  const callExpr = callExprPath.node;
  // Check that there are at least 2 arguments (identifier, args)
  if (callExpr.arguments.length < 2) {
    const errors = [
      'Translation component must have at least 2 arguments (identifier, args)',
    ];
    return { errors };
  }

  // Get the component args
  const args = callExpr.arguments[1];
  if (!t.isObjectExpression(args)) {
    const errors = [
      'Translation component must have an object expression as the second argument',
    ];
    return { errors };
  }
  const argsPath = callExprPath.get('arguments')[1];
  if (!argsPath?.isObjectExpression()) {
    const errors = [
      'Translation component must have an object expression as the second argument',
    ];
    return { errors };
  }

  // Map to appropriate validation function
  switch (canonicalName) {
    case GT_COMPONENT_TYPES.T:
    case GT_COMPONENT_TYPES.GtInternalTranslateJsx:
      return validateTComponentArgs(
        argsPath as NodePath<t.ObjectExpression>,
        state
      );
    default:
      const errors = [
        `Invalid translation component: ${canonicalName}. You likely passed a non-translation component to validateTranslationComponentArgs`,
      ];
      return { errors };
  }
}

/* =============================== */
/* Helper Functions */
/* =============================== */

function validateTComponentArgs(
  argsPath: NodePath<t.ObjectExpression>,
  state: TransformState
): {
  errors: string[];
  _hash?: string;
  id?: string;
  context?: string;
  maxChars?: number;
  requiresReview?: boolean;
  children?: JsxChildren;
  hasDeriveContext?: boolean;
  hasDeriveChildren?: boolean;
} {
  const errors: string[] = [];
  const args = argsPath.node;

  // Validate id
  const idValidation = validateStringProperty(args, 'id');
  errors.push(...idValidation.errors);
  const id = idValidation.value;

  // Validate context (with derive fallback)
  const contextValidation = validateStringProperty(args, 'context', state);
  errors.push(...contextValidation.errors);
  const context = contextValidation.value;
  // Validate maxChars
  const maxCharsValidation = validateNumberProperty(args, 'maxChars');
  errors.push(...maxCharsValidation.errors);
  const maxChars = maxCharsValidation.value;

  // Validate requiresReview
  const requiresReviewValidation = validateBooleanProperty(
    args,
    'requiresReview'
  );
  errors.push(...requiresReviewValidation.errors);
  const requiresReview = requiresReviewValidation.value;

  // Validate hash
  const hashValidation = validateStringProperty(args, '_hash');
  errors.push(...hashValidation.errors);
  const _hash = hashValidation.value;

  // Validate children
  const childrenValidation = validateChildrenProperty(argsPath, state);
  errors.push(...childrenValidation.errors);
  const children = childrenValidation.value;

  const hasDeriveContext =
    contextValidation.hasDeriveExpression || childrenValidation.hasAutoderive;

  return {
    errors,
    id,
    context,
    _hash,
    maxChars,
    requiresReview,
    children,
    hasDeriveContext,
    hasDeriveChildren: childrenValidation.containsDerive,
  };
}

/**
 * Validate that the children property is a string literal
 */
export function validateChildrenProperty(
  argsPath: NodePath<t.ObjectExpression>,
  state: TransformState
): {
  errors: string[];
  value?: JsxChildren;
  hasAutoderive?: boolean;
  containsDerive?: boolean;
} {
  const errors: string[] = [];

  // Get the children property
  const childrenValidation =
    validateChildrenPropertyFromObjectExpression(argsPath);
  if (childrenValidation.errors.length > 0) {
    errors.push(...childrenValidation.errors);
    return { errors };
  }
  const children = childrenValidation.value;
  if (children === undefined) {
    errors.push(`The <${GT_COMPONENT_TYPES.T}> component must have children`);
    return { errors };
  }

  // constructJsxChildren now returns JsxValidationError[]
  const validation = constructJsxChildren(children, state);

  // Autoderive: filter out dynamic-content errors
  let hasAutoderive = false;
  let filteredErrors: JsxValidationError[] = validation.errors;
  if (state.settings.autoderive.jsx) {
    filteredErrors = validation.errors.filter(
      (e) => e.type !== 'dynamic-content'
    );
    hasAutoderive = filteredErrors.length < validation.errors.length;
  }

  // Convert structured errors back to strings
  errors.push(...filteredErrors.map((e) => e.message));
  const value = validation.value;

  return {
    errors,
    value,
    hasAutoderive,
    containsDerive: validation.containsDerive,
  };
}

// String Literal Functions

/**
 * Will validate and retrieve property from object expression and also try prefixing with a $
 *
 */
function validateStringProperty(
  args: t.ObjectExpression,
  name: string,
  state?: TransformState
): {
  errors: string[];
  value?: string;
  hasDeriveExpression?: boolean;
} {
  const errors: string[] = [];

  // Get the property
  const property =
    getObjectPropertyFromObjectExpression(args, name) ||
    getObjectPropertyFromObjectExpression(args, '$' + name);
  if (!property) {
    return { errors };
  }

  // Validate property
  const validation = validateObjectPropertyIsStringLiteral(property, name);
  if (validation.errors.length === 0) {
    return { errors, value: validation.value };
  }

  // String validation failed — check if it's a valid derive() expression
  if (state && t.isObjectProperty(property) && t.isExpression(property.value)) {
    const deriveErrors: string[] = [];
    validateDerive(property.value, state, deriveErrors);
    if (deriveErrors.length === 0) {
      return { errors, hasDeriveExpression: true };
    }
  }

  // Neither string nor derive — return original errors
  errors.push(...validation.errors);
  return { errors };
}

/**
 * Validate that an object property is a string literal
 */
function validateObjectPropertyIsStringLiteral(
  arg: t.SpreadElement | t.ObjectMethod | t.ObjectProperty,
  name: string
): {
  errors: string[];
  value?: string;
} {
  const errors: string[] = [];

  // Validate the property is a string literal from an expression
  if (
    !t.isObjectProperty(arg) ||
    !t.isExpression(arg.value) ||
    !validateExpressionIsStringLiteral(arg.value)
  ) {
    errors.push(
      `The <${GT_COMPONENT_TYPES.T}> component must have a string literal in its ${name} field`
    );
    return { errors };
  }

  // Get the string literal
  const value = getStringLiteralFromExpression(arg.value);
  return { errors, value };
}

/**
 * Given an expression, return the string literal (throws an error if not a string literal)
 */
function getStringLiteralFromExpression(
  expr: t.Expression
): string | undefined {
  if (t.isStringLiteral(expr)) {
    return expr.value;
  }
  if (t.isTemplateLiteral(expr)) {
    if (expr.expressions.length === 0) {
      return expr.quasis[0]?.value.cooked || '';
    }
  }
}

// Number Literal Functions

/**
 * Will validate and retrieve property from object expression and also try prefixing with a $
 */
function validateNumberProperty(
  args: t.ObjectExpression,
  name: string
): {
  errors: string[];
  value?: number;
} {
  const errors: string[] = [];

  // Get the property
  const property =
    getObjectPropertyFromObjectExpression(args, name) ||
    getObjectPropertyFromObjectExpression(args, '$' + name);
  if (!property) {
    return { errors };
  }

  // Validate property
  const validation = validateObjectPropertyIsNumberLiteral(property, name);
  errors.push(...validation.errors);
  const value = validation.value;

  return { errors, value };
}

/**
 * Validate that an object property is a number literal
 */
function validateObjectPropertyIsNumberLiteral(
  arg: t.SpreadElement | t.ObjectMethod | t.ObjectProperty,
  name: string
): {
  errors: string[];
  value?: number;
} {
  const errors: string[] = [];

  // Validate the property is a string literal from an expression
  if (
    !t.isObjectProperty(arg) ||
    !t.isExpression(arg.value) ||
    !validateExpressionIsNumericLiteral(arg.value)
  ) {
    errors.push(
      `The <${GT_COMPONENT_TYPES.T}> component must have a number literal in its ${name} field`
    );
    return { errors };
  }

  // Get the number literal
  const value = getNumberLiteralFromExpression(arg.value);

  // Validate it is an integer
  if (value !== undefined && !validateNumberLiteralIsInteger(value)) {
    errors.push(
      `The <${GT_COMPONENT_TYPES.T}> component must have an integer in its ${name} field`
    );
    return { errors };
  }

  return { errors, value };
}

/**
 * Given an expression, return the number literal (throws an error if not a number literal)
 */
function getNumberLiteralFromExpression(
  expr: t.Expression
): number | undefined {
  if (t.isNumericLiteral(expr)) {
    return expr.value;
  }
  // handle numbers with unary operators
  if (t.isUnaryExpression(expr) && t.isNumericLiteral(expr.argument)) {
    // On purpose, we are taking the absolute value of the number literal
    return expr.argument.value;
  }
}

/**
 * Validate that a number literal is an integer
 */
function validateNumberLiteralIsInteger(value: number): boolean {
  return Number.isInteger(value);
}

// Boolean Literal Functions

/**
 * Will validate and retrieve property from object expression and also try prefixing with a $.
 * Only boolean literals are accepted — string "true"/"false" and dynamic
 * expressions are rejected because the value is hash-changing.
 */
function validateBooleanProperty(
  args: t.ObjectExpression,
  name: string
): {
  errors: string[];
  value?: boolean;
} {
  const errors: string[] = [];

  // Get the property
  const property =
    getObjectPropertyFromObjectExpression(args, name) ||
    getObjectPropertyFromObjectExpression(args, '$' + name);
  if (!property) {
    return { errors };
  }

  if (
    !t.isObjectProperty(property) ||
    !t.isExpression(property.value) ||
    !validateExpressionIsBooleanLiteral(property.value)
  ) {
    errors.push(
      `The <${GT_COMPONENT_TYPES.T}> component must have a boolean literal (true or false) in its ${name} field`
    );
    return { errors };
  }

  return { errors, value: (property.value as t.BooleanLiteral).value };
}
